import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'pts_install_dismissed_at';

export const isStandalone = () =>
  (typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true));

export const isIos = () => typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

/** Register the portal service worker (only called from /easer and /client). */
export const registerPortalServiceWorker = () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (window.location.hostname === 'localhost' && process.env.NODE_ENV === 'development') return;
  navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' })
    .then((reg) => { try { reg.update(); } catch (_) { /* ignore */ } })
    .catch(() => { /* unsupported / blocked */ });
  // A new service worker took control (app updated): reload once so every screen runs the same, latest code.
  // Not on the very first install (no previous controller): nothing stale to replace.
  const hadController = Boolean(navigator.serviceWorker.controller);
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController || reloaded) return;
    reloaded = true;
    window.location.reload();
  });
};

/**
 * Force-update the portal: fetch the latest service worker, drop every cache, then reload with fresh code.
 * Resolves with { updated: boolean } — `updated` is true when a newer service worker was found.
 */
export const forceAppUpdate = async () => {
  let updated = false;
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const before = reg.active && reg.active.scriptURL;
        await reg.update();
        const waiting = reg.waiting || reg.installing;
        if (waiting) {
          updated = true;
          waiting.postMessage('SKIP_WAITING');
          await new Promise((resolve) => {
            const t = setTimeout(resolve, 3000);
            navigator.serviceWorker.addEventListener('controllerchange', () => { clearTimeout(t); resolve(); }, { once: true });
          });
        } else if (before && reg.active && reg.active.scriptURL !== before) {
          updated = true;
        }
      }
    } catch (_) { /* no SW (private mode, old browser) — still clear caches and reload */ }
  }
  if (typeof caches !== 'undefined') {
    try { const keys = await caches.keys(); await Promise.all(keys.map((k) => caches.delete(k))); } catch (_) { /* ignore */ }
  }
  return { updated };
};

/*
 * `beforeinstallprompt` fires ONCE per page load, often before the screen that shows the install button is
 * mounted (ex. GRH > Paramètres opened later). It is therefore captured here, at module load, and shared with
 * every component through a tiny store — otherwise the button would never get the one-click prompt.
 */
const store = { prompt: null, installed: false, listeners: new Set() };
const notify = () => store.listeners.forEach((fn) => fn());
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); store.prompt = e; notify(); });
  window.addEventListener('appinstalled', () => { store.installed = true; store.prompt = null; notify(); });
}

/**
 * Install state for the "Ajouter à l'écran d'accueil" card.
 * - Chrome/Android/desktop: uses the captured beforeinstallprompt and exposes install().
 * - iOS Safari: no prompt API, exposes needsIosHint so the UI shows the manual steps.
 */
export const useInstallPrompt = () => {
  const [prompt, setPrompt] = useState(store.prompt);
  const [installed, setInstalled] = useState(isStandalone() || store.installed);
  const [dismissed, setDismissed] = useState(() => {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at && Date.now() - at < 7 * 24 * 3600 * 1000; // hidden for a week after "Plus tard"
  });

  useEffect(() => {
    const sync = () => { setPrompt(store.prompt); if (store.installed) setInstalled(true); };
    store.listeners.add(sync);
    sync();
    return () => { store.listeners.delete(sync); };
  }, []);

  const install = useCallback(async () => {
    const p = prompt || store.prompt;
    if (!p) return false;
    p.prompt();
    const choice = await p.userChoice.catch(() => ({ outcome: 'dismissed' }));
    store.prompt = null; // a prompt can only be used once
    if (choice.outcome === 'accepted') { store.installed = true; setInstalled(true); }
    setPrompt(null);
    notify();
    return choice.outcome === 'accepted';
  }, [prompt]);

  const dismiss = useCallback(() => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setDismissed(true); }, []);

  return { canPrompt: !!prompt, install, installed, dismissed, dismiss, needsIosHint: isIos() && !installed && !prompt };
};
